import style from "@/styles/pages/AnswerSecurity/AnswerSecurity.module.css";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useState } from "react";
import { setApplicantId, setCredentialsCreated, setDateOfBirthDay, setDateOfBirthMonth, setDateOfBirthYear, setEmailAddress, setExistingMemberFields, setFirstName, setFmApplicantId, setFmApplicationId, setLastName, setMaritalStatus, setMiddleName, setNumOfDependents, setPhoneNumber, setPhoneType, setResidentStatus, setSin, setTitle } from "@/slices/applicantSlice";
import { americanStatesAbbreviationList, processUserAppData } from "@/lib/util";
import { RootState } from "@/store/dtm";
import { AmendMemmberCoApplicantCommand, AnswerChallengeDto, ApplicationDto, RememberClientDto } from "@/lib/DTMWebAPIClient.generated";
import { useAuth } from "@/context/AuthContext";
import FadeTransition from "@/styles/animation/FadeTransition";
import { setStreetNumber, setStreetName, setUnitNumber, setCity, setProvince, setPostalCode, setCountry, setStreetType, setUnitType, setAddressId, setStreetDirection } from "@/slices/homeAddressSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { setAMLFieldsCompleted } from "@/slices/legalTermsSlice";
import { streetTypes } from "@/ts/streetTypes";
import { selectFundmoreSSO } from "@/slices/sessionDataSlice";

export default function AnswerSecurity() {
    const client = createApiClient({ timeout: 60000 });

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const getAmericanStates = americanStatesAbbreviationList();
    const { login } = useAuth();

    const [challengeResponse, setChallengeResponse] = useState("");
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(<span>The answer provided does not match our records.</span>)
    const [verifying, setVerifying] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [signInText, setSignInText] = useState("Continue");

    const credential = useSelector(
        (state: RootState) => state.credential.credential
    );
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const twoFactorAuth = useSelector(
        (state: RootState) => state.twoFactorAuth
    );
    const sessionData = useSelector(selectFundmoreSSO);


    const handleClick = () => {
        //Validate login
        setDisplayError(false);
        setVerifying(true);

        if (challengeResponse.trim() == "") {
            setDisplayError(true);
            setErrorMessage(<span>The Answer field is required.</span>);
            setVerifying(false);
            return;
        }

        client.apiAuthOlbloginAnswerchallenge(new AnswerChallengeDto({
            username: credential.email,
            sessionId: twoFactorAuth.authTwoFa.sessionId,
            answer: challengeResponse,
            dealId: application.id,
            docIqToken: sessionData?.request
        })).then(async function (result) {
            dispatch(setCredentialsCreated(true));
            login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
            let authenticatedClient = createApiClient({ timeout: 60000, token: result.accessToken });

            if (result.applicant) {
                let applicantInfo = result.applicant;

                //Check if this member's start date is less than 60 days; if so, redirect to error page
                if (result.applicant?.startDate?.year() && result.applicant.startDate.year() > 1901) {
                    let today = dayjs();
                    if (today.diff(result.applicant.startDate, 'day') < 60) {
                        navigate("/error/1", { replace: true });
                        return;
                    }
                }

                let dobMonth = applicantInfo.dateOfBirth.month() + 1;
                let dobDay = applicantInfo.dateOfBirth.date();

                //IMPORTANT: If the co-applicant is an existing member, replace the temporary applicant (that was added by the primary) with the one that
                //is already tied to the existing member
                if (applicant.id != 0) {
                    await authenticatedClient.apiDealsApplicantMemberAmend(new AmendMemmberCoApplicantCommand({
                        temporaryApplicantId: applicant.id,
                    })).then((res) => {
                        console.log(res);
                        // Handle response if needed
                    }).catch((error) => {
                        // Handle error if needed
                    });
                }


                dispatch(setApplicantId(applicantInfo.id ? applicantInfo.id : 0));
                dispatch(setFmApplicationId(applicantInfo.fmApplicationId ? applicantInfo.fmApplicationId : ""));
                dispatch(setFmApplicantId(applicantInfo.fmApplicantId ? applicantInfo.fmApplicantId : ""));
                dispatch(setTitle(applicantInfo.title ? applicantInfo.title : ""));
                dispatch(setFirstName(applicantInfo.firstName));
                dispatch(setMiddleName(applicantInfo.middleName ? applicantInfo.middleName : ""));
                dispatch(setLastName(applicantInfo.lastName));
                dispatch(setPhoneNumber(applicantInfo.phoneNumber));
                dispatch(setPhoneType(applicantInfo.phoneType ? applicantInfo.phoneType.replace(/\w/, p => p.toUpperCase()) : ""));
                dispatch(setEmailAddress(applicantInfo.email));
                dispatch(setDateOfBirthYear(applicantInfo.dateOfBirth.year() + ""));
                dispatch(setDateOfBirthMonth(dobMonth.toString().length == 1 ?
                    "0" + dobMonth.toString() : dobMonth.toString()));
                dispatch(setDateOfBirthDay(dobDay + ""));
                dispatch(setMaritalStatus(applicantInfo.maritalStatus ? applicantInfo.maritalStatus : ""));
                dispatch(setNumOfDependents(applicantInfo.numberOfDependents ? applicantInfo.numberOfDependents : 0));

                if (applicantInfo.address) {
                    dispatch(setAddressId(applicantInfo.address[0].id ? applicantInfo.address[0].id : 0));
                    dispatch(setStreetNumber(applicantInfo.address[0].streetNumber ? applicantInfo.address[0].streetNumber : ""));
                    dispatch(setStreetName(applicantInfo.address[0].streetName ? applicantInfo.address[0].streetName : ""));
                    dispatch(setStreetDirection(applicantInfo.address[0].streetDirection ? applicantInfo.address[0].streetDirection : ""));
                    dispatch(setUnitType(applicantInfo.address[0].unitType ? applicantInfo.address[0].unitType : ""));
                    dispatch(setUnitNumber(applicantInfo.address[0].unitNumber ? applicantInfo.address[0].unitNumber : ""));
                    dispatch(setCity(applicantInfo.address[0].city ? applicantInfo.address[0].city : ""));
                    //If the country is set to United States but the province/state does not match a US state, do not set the province value as it is likely a Canadian province that was not properly cleared
                    if (applicantInfo.address[0].country == "United States" && !getAmericanStates.includes(applicantInfo.address[0].region ? applicantInfo.address[0].region : "")) {
                        dispatch(setProvince(""));
                    } else {
                        dispatch(setProvince(applicantInfo.address[0].region ? applicantInfo.address[0].region : ""));
                    }
                    dispatch(setPostalCode(applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode : ""));
                    dispatch(setCountry(applicantInfo.address[0].country));
                    dispatch(setResidentStatus(applicantInfo.address[0].residenceStatusType_Name ? applicantInfo.address[0].residenceStatusType_Name : ""));

                    //Attempt to parse the street type based on whether it contains multiple words
                    if ((applicantInfo.address[0].streetType == undefined || applicantInfo.address[0].streetType == "") && applicantInfo.address[0].streetName && applicantInfo.address[0].streetName.split(" ").length > 1) {
                        let streetNameComponents = applicantInfo.address[0].streetName.split(" ");
                        let newStreetName = "";
                        for (let i = 0; i < streetNameComponents.length - 1; i++) {
                            newStreetName += streetNameComponents[i] + " ";
                        }
                        let streetType = streetNameComponents[streetNameComponents.length - 1].toUpperCase();
                        dispatch(setStreetType(streetTypes.includes(streetType) ? streetType : ""));
                        dispatch(setStreetName(newStreetName.trim()));
                    } else if (applicantInfo.address[0].streetType != undefined && applicantInfo.address[0].streetType != "") {
                        dispatch(setStreetType(applicantInfo.address[0].streetType));
                    }
                }

                //Check Validity of Ovation Fields
                let phoneNumberReg = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/);
                dispatch(setExistingMemberFields({
                    isFirstNameAvailable: applicantInfo.firstName.trim() != '',
                    isLastNameAvailable: applicantInfo.lastName.trim() != '',
                    isPhoneNumberAvailable: phoneNumberReg.test(applicantInfo.phoneNumber),
                    isSinAvailable: true,
                    isProvinceAvailable: applicantInfo.address != undefined && applicantInfo.address[0].region ? applicantInfo.address[0].region.trim() != '' : false,
                    isPostalCodeAvailable: applicantInfo.address != undefined && applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode.trim() != '' : false,
                    isCountryAvailable: applicantInfo.address != undefined && applicantInfo.address[0].country ? applicantInfo.address[0].country.trim() != '' : false
                }));

            }

            authenticatedClient.apiApplicationsFetch().then(async (appData) => {
                setVerifying(false);

                if (rememberDevice) {
                    await authenticatedClient.apiAuthOlbloginRememberclient(new RememberClientDto({
                        sessionId: twoFactorAuth.authTwoFa.sessionId,
                    })).then(function (result) {
                        Cookies.set(credential.email, result.clientToken ? result.clientToken : "");
                    }).catch((error) => {
                        console.log(error);
                    });
                }

                if (appData) {

                    //Prefil member info such as subject property, address, etc.
                    processUserAppData(appData, dispatch, false);
                    dispatch(setAMLFieldsCompleted(true));

                    if (result.docIqDestination == undefined || result.docIqDestination == "") {
                        navigate("/home", { replace: true });
                        return;
                    } else {
                        setVerifying(true);
                        setSignInText("Redirecting to Document Portal....");
                        window.location.replace(result.docIqDestination);
                    }
                } else {
                    navigate("/confirm-consent", { replace: true });
                }
            }).catch((error) => {
                setVerifying(false);
                console.log("Error fetching application info", error);
            });

        }).catch((error) => {
            setVerifying(false);
            setDisplayError(true);
            if (error.response.code && error.response.type == "NewMemberException") {
                navigate("/error/1", { replace: true });
            } else if (error.response.code && error.response.type == "JointMemberException") {
                navigate("/login", { replace: true, state: { invalidMember: true } });
            } else if (error.response.code && error.response.code == 1000) {
                navigate("/error/9", { replace: true });
            } else {
                setErrorMessage(error.response.message);
            }

        });
    }

    const cancel = () => {
        navigate("/login");
    }

    return (
        <main aria-labelledby="main-content">
            <div className="main-container max-md:mt-10">
                <FadeTransition>
                    <div className={clsx(style.login, "container", "mt-10")}>
                        <div className={style.emHeading}>
                            <div className="grid grid-cols-1">
                                <h1>Security Question</h1>
                                <p>The question below have been selected to</p>
                                <p>identify your Membership. Please answer to continue.</p>
                            </div>
                        </div>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    {errorMessage}
                                </span>
                            </div>
                        }
                        <div className="mt-5">
                            <label htmlFor="email">{twoFactorAuth.authTwoFa.challengeQuestion?.question}</label>
                            <div className="flex relative">
                                {/* <span>
                                <img
                                    src="assets/error-icon.svg"
                                    className={clsx("w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                    )}
                                    alt="error"
                                    height={5}
                                    width={5}
                                />
                            </span> */}
                                <input
                                    className={clsx(
                                        "mt-2"
                                    )}
                                    type="password"
                                    id="challengeAnswer"
                                    value={challengeResponse}
                                    onChange={(e) => {
                                        setChallengeResponse(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="mt-5 flex flex-col-reverse">
                            <label></label>
                            <input
                                id="skipprompt"
                                type="checkbox"
                                value={rememberDevice ? "true" : "false"}
                                onChange={(e) => setRememberDevice(e.target.checked)}
                                className="accent"
                            />
                            <label htmlFor="skipprompt">
                                <p className="sub-heading ml-7 -mt-5">
                                    Don't Ask Me Again
                                </p>
                            </label>
                        </div>
                        <div className="flex relative">
                            <button
                                onClick={handleClick}
                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                disabled={verifying}>
                                {verifying ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}>
                                        <FontAwesomeIcon
                                            icon={faSpinner}
                                            spin
                                            style={{ marginRight: 8 }}
                                            className={clsx(verifying ? "" : "hidden")}
                                        />
                                    </motion.div>) : null}
                                {signInText}
                            </button>
                        </div>
                        <div className="flex relative">
                            <button
                                onClick={cancel}
                                className={clsx("secondary mt-5 w-full sm:w-28 h-11 px-6", style.secondary)}>
                                Cancel
                            </button>
                        </div>
                        <div className="text-center mt-5">
                            <p className="mt-5 mb-3">If you're having difficulty signing in, please call</p>
                            <p className="active">1-866-592-2226</p>
                        </div>
                    </div>
                </FadeTransition>
            </div>
        </main>
    );
}
